import { uiPaper } from "shared/ui/uiSurface"
import { PersonAdd } from "@mui/icons-material"
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  AvailableCourier,
  useBatchAssignOrdersMutation,
  useGetAvailableCouriersQuery,
} from "../../apis/appApi"

type AssignCourierDialogProps = {
  open: boolean
  onClose: () => void
  orderIds: number[]
  onSuccess: (succeeded: number, failed: number) => void
}

export const AssignCourierDialog: FC<AssignCourierDialogProps> = ({
  open,
  onClose,
  orderIds,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [selectedCourier, setSelectedCourier] = useState<AvailableCourier | null>(null)

  const { data: couriersResponse, isLoading: couriersLoading } =
    useGetAvailableCouriersQuery(undefined, { skip: !open })
  const couriers = couriersResponse?.data ?? []

  const [batchAssign, { isLoading: assigning }] = useBatchAssignOrdersMutation()

  const handleConfirm = async () => {
    if (!selectedCourier || orderIds.length === 0) return

    try {
      const result = await batchAssign({
        orderIds,
        haulerEmployeeId: selectedCourier.id,
      }).unwrap()

      const succeeded = result.results.filter((r) => r.success).length
      const failed = result.results.filter((r) => !r.success).length
      onSuccess(succeeded, failed)
    } catch {
      onSuccess(0, orderIds.length)
    }

    setSelectedCourier(null)
    onClose()
  }

  const handleClose = () => {
    setSelectedCourier(null)
    onClose()
  }

  return (
    <Dialog
      slotProps={{ paper: uiPaper("UI-DLV2-030") }}
      data-testid="assign-courier-dialog"
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PersonAdd color="primary" />
          <Typography variant="h6">
            {t("ordersPage.assignCourier.title")}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("ordersPage.confirmMessage", {
            action: t("ordersPage.actions.assignCourier"),
            count: orderIds.length,
          })}
        </Typography>

        <Autocomplete
          data-testid="assign-courier-select"
          options={couriers}
          loading={couriersLoading}
          value={selectedCourier}
          onChange={(_e, value) => setSelectedCourier(value)}
          getOptionLabel={(option) =>
            `${option.firstName} ${option.lastName} (${option.haulerName})`
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          groupBy={(option) => option.haulerName}
          noOptionsText={t("ordersPage.assignCourier.noCouriers")}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("ordersPage.assignCourier.selectCourier")}
              placeholder={t("ordersPage.assignCourier.searchPlaceholder")}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Stack>
                <Typography variant="body2" fontWeight={500}>
                  {option.firstName} {option.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.haulerName}
                </Typography>
              </Stack>
            </li>
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          data-testid="assign-courier-cancel-btn"
          onClick={handleClose}
          disabled={assigning}
        >
          {t("cancel")}
        </Button>
        <Button
          data-testid="assign-courier-confirm-btn"
          onClick={handleConfirm}
          variant="contained"
          disabled={assigning || !selectedCourier}
        >
          {assigning ? (
            <CircularProgress size={18} sx={{ mr: 1 }} />
          ) : null}
          {t("ordersPage.assignCourier.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
